import { useEffect, useRef } from 'react';
import { COULEURS } from '../../app/styles.js';
import { mettreEnPage } from './arbre-dessine-calcul.js';
import { Noeud, type ContexteNoeud } from './noeud.js';
import { nomAffiche, personneParId } from './modele.js';

/** Taille d'une carte : fixe, pour que les liens partent et arrivent exactement aux bords ; plus grande côté famille (18 px). */
const TAILLE_EQUIPE = { largeur: 210, hauteur: 96 };
const TAILLE_FAMILLE = { largeur: 260, hauteur: 150 };
/** Carte d'équipe avec le bouton « Ajouter un frère ou une sœur » (44 px de haut au moins, cible tactile). */
const TAILLE_EQUIPE_ACTION = { largeur: 210, hauteur: 150 };

/**
 * Le vrai arbre : l'auteur décédé en haut, les couples côte à côte, un lien parent → enfant par enfant. Le dessin est
 * décoratif (aria-hidden) : le sens est porté par l'arbre ARIA (cartes, niveaux) et par la version liste.
 */
export function ArbreDessine({ ctx }: { ctx: ContexteNoeud }) {
  const fratrie = ctx.vue === 'equipe' ? ctx.onAjouterFratrie : undefined;
  const taille = ctx.vue === 'famille' ? TAILLE_FAMILLE : fratrie ? TAILLE_EQUIPE_ACTION : TAILLE_EQUIPE;
  const m = mettreEnPage(ctx.donnees, taille);
  const racine = useRef<HTMLDivElement>(null);
  // Sur petit écran, le défilement horizontal s'ouvre sur les auteurs (rangée du haut), pas sur une marge vide.
  useEffect(() => {
    const defileur = racine.current?.parentElement;
    const haut = m.noeuds.filter((n) => n.rang === 0);
    if (!defileur || haut.length === 0 || defileur.scrollWidth <= defileur.clientWidth) return;
    const gauche = Math.min(...haut.map((n) => n.x));
    const droite = Math.max(...haut.map((n) => n.x + taille.largeur));
    defileur.scrollLeft = Math.max(0, (gauche + droite) / 2 - defileur.clientWidth / 2);
    // Une seule fois, à l'affichage de l'arbre.
  }, []);
  return (
    <div ref={racine} data-disposition="dessine" style={{ position: 'relative', width: m.largeur, height: m.hauteur, margin: '12px 0' }}>
      <svg aria-hidden="true" focusable="false" width={m.largeur} height={m.hauteur} style={{ position: 'absolute', left: 0, top: 0 }}>
        {m.liens.map((l, i) => (
          <path
            key={`${l.type}-${l.de}-${l.vers}-${i}`}
            d={l.chemin}
            data-lien={l.type}
            data-parent={l.type === 'filiation' ? l.parents.join(' ') : l.de}
            data-enfant={l.vers}
            fill="none"
            stroke={COULEURS.marine}
            strokeWidth={l.type === 'union' ? 3 : 2}
            {...(l.secondaire ? { strokeDasharray: '6 4' } : {})}
          />
        ))}
      </svg>
      {m.noeuds.map((n) => {
        const p = personneParId(ctx.donnees, n.id);
        if (!p) return null;
        return (
          <div
            key={n.id}
            data-rang={n.rang}
            {...(n.sansLien ? { 'data-sans-lien': 'true' } : {})}
            style={{ position: 'absolute', left: n.x, top: n.y, width: taille.largeur, height: taille.hauteur, display: 'grid' }}
          >
            <Noeud p={p} ctx={ctx} />
            {fratrie ? (
              <button
                type="button"
                data-ajouter-fratrie={p.id}
                aria-label={`Ajouter un frère ou une sœur de ${nomAffiche(p)}`}
                // Entrée et Espace activent le bouton, pas la navigation de l'arbre.
                onKeyDown={(e) => e.stopPropagation()}
                onClick={() => fratrie(p.id)}
                style={{
                  minHeight: 44,
                  marginTop: 4,
                  padding: '0 8px',
                  borderRadius: 8,
                  border: `1px solid ${COULEURS.marine}`,
                  background: COULEURS.blanc,
                  color: COULEURS.marine,
                  font: 'inherit',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Ajouter un frère ou une sœur
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
